import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { cn } from '../../ui/cn';
import { HlmIconComponent } from '../../ui/icon.component';
import { typeBadgeVariant } from '../utils/reachability-type-badge';

/** Active-chip tint per badge variant — mirrors HlmBadgeComponent's recipe. */
const ACTIVE_CLASS: Record<string, string> = {
  default: 'border-transparent bg-primary/20 text-primary',
  secondary: 'border-transparent bg-secondary text-secondary-foreground',
  accent: 'border-transparent bg-accent/15 text-accent',
  success: 'border-transparent bg-success/15 text-success',
  purple: 'border-transparent bg-ring-purple/15 text-ring-purple',
  teal: 'border-transparent bg-chip-teal/15 text-chip-teal',
  rose: 'border-transparent bg-chip-rose/15 text-chip-rose',
  outline: 'border-foreground/30 bg-foreground/10 text-foreground',
};

/** Deselected chip: dashed outline + eye-off icon, so "hidden" reads at a glance. */
const HIDDEN_CLASS = 'border-dashed border-muted-foreground/70 bg-muted text-muted-foreground line-through decoration-2 hover:text-foreground hover:border-foreground/50';

/**
 * Quick filter for reachability types — a row of toggle chips. Selecting a chip
 * keeps that type visible; deselected types are filtered out by the parent.
 * Chip colors match the shared {@link typeBadgeVariant} scheme. While any type is
 * hidden, a "N hidden · Show all" hint offers a one-click reset.
 *
 * Stateless: the parent owns the selected set (`selected` in / `selectedChange`
 * out). Defaulting PUSH off is the parent's responsibility.
 */
@Component({
  selector: 'cmt-type-filter',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmIconComponent],
  template: `
    @if (types().length > 1) {
      <div class="flex flex-wrap items-center gap-1.5">
        <span class="mr-0.5 inline-flex items-center gap-1 font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">
          <hlm-icon name="lucideFilter" size="0.8rem" /> Show types
        </span>
        @for (t of types(); track t) {
          <button type="button"
            [class]="chipClass(t)"
            [attr.aria-pressed]="isSelected(t)"
            [attr.title]="isSelected(t) ? 'Hide ' + t : 'Show ' + t"
            (click)="toggle(t)">
            @if (!isSelected(t)) {
              <hlm-icon name="lucideEyeOff" size="0.9rem" />
            }
            {{ t }}
          </button>
        }
        @if (hiddenCount() > 0) {
          <span class="ml-auto inline-flex items-center gap-1.5 text-sm text-muted-foreground">
            {{ hiddenCount() }} hidden ·
            <button type="button" class="font-medium text-primary hover:underline" (click)="showAll()">Show all</button>
          </span>
        }
      </div>
    }
  `,
})
export class TypeFilterComponent {
  /** All types present in the data, in display order. */
  readonly types = input<string[]>([]);
  /** Currently-selected (visible) types. */
  readonly selected = input<string[]>([]);
  readonly selectedChange = output<string[]>();

  private readonly selectedSet = computed(() => new Set(this.selected()));

  /** Number of available types currently deselected. */
  readonly hiddenCount = computed(() => this.types().filter(t => !this.selectedSet().has(t)).length);

  isSelected(type: string): boolean {
    return this.selectedSet().has(type);
  }

  chipClass(type: string): string {
    return cn(
      'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-sm font-semibold leading-tight transition-colors',
      this.isSelected(type)
        ? ACTIVE_CLASS[typeBadgeVariant(type)] ?? ACTIVE_CLASS['outline']
        : HIDDEN_CLASS,
    );
  }

  toggle(type: string): void {
    const next = new Set(this.selectedSet());
    if (next.has(type)) {
      next.delete(type);
    } else {
      next.add(type);
    }
    this.selectedChange.emit([...next]);
  }

  showAll(): void {
    this.selectedChange.emit([...this.types()]);
  }
}
